import React, { useState, useEffect, useRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  FaEnvelopeOpenText,
  FaShieldAlt,
  FaRedo,
  FaLock,
} from "react-icons/fa";

import Swal from "sweetalert2";
import "../../../assets/css/General/otpverification.css";
import logo from "../../../assets/images/logo.png";


const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

/**
 * Reusable OTP Verification screen for both Buyer & Seller flows.
 *
 * Props:
 *  - email        : string  -> email address OTP was sent to
 *  - userType     : "buyer" | "seller"
 *  - verifyUrl    : string  -> API endpoint to verify OTP (e.g. "/api/buyer/verify-otp")
 *  - resendUrl    : string  -> API endpoint to resend OTP (e.g. "/api/buyer/resend-otp")
 *  - onVerified   : fn(responseData) -> called after successful verification
 */
function OtpVerification({
  email = "abc@example.com",
  userType,
  handleVerify,
  handleResend,
  loading,
  resendLoading,
}) {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [errorIndex, setErrorIndex] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);

  const inputsRef = useRef([]);

  /* ------------- Countdown Timer ------------- */
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const formatTime = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  /* ------------- Input Handling ------------- */
  const handleChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return; // digits only

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    setErrorIndex(false);

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pasted)) return;
    e.preventDefault();
    const digits = pasted.slice(0, OTP_LENGTH).split("");
    const updated = Array(OTP_LENGTH).fill("");
    digits.forEach((d, i) => (updated[i] = d));
    setOtp(updated);
    inputsRef.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
  };

  return (
    <div className="otp-page">
      <div className="otp-wrapper">
        {/* ================= LEFT PANEL ================= */}
        <div className="otp-left">
          <div className="otp-left-brand">
            <img src={logo} alt="Estate Experts" className="otp-left-logo" />
            <div className="otp-left-brand-text-wrap">
              <span className="otp-left-brand-text">Estate Experts</span>
              <span className="otp-left-brand-tagline">
                Find Your Dream Property
              </span>
            </div>
          </div>

          <div className="otp-left-content">
            <h2 className="otp-left-heading">
              Your <span className="otp-left-heading-accent">Security</span>,
              <br />
              Our Priority
            </h2>
            <p className="otp-left-text">
              We have sent a 6-digit code to your email address to verify
              your {userType === "seller" ? "seller" : "buyer"} account.
            </p>
          </div>

          <div className="otp-left-image-wrap">
            <img
              src={'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS4qeUHiNienum14OMbe8qlmpVFu1SoHxFKZ8mcVp3Oe8T5nph9hgXSSK8&s=10'}
              alt="Estate Experts Property"
              className="otp-left-image"
            />
          </div>
        </div>

        {/* ================= RIGHT PANEL ================= */}
        <div className="otp-right">
          <div className="otp-icon-circle">
            <FaEnvelopeOpenText />
          </div>

          <h3 className="otp-heading">Verify Your Email</h3>
          <p className="otp-subtext">
            Enter the 6-digit code we have sent to
          </p>
          <span className="otp-email">{email}</span>

          {/* OTP Inputs */}
          <div className="otp-inputs-row" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`otp-input-box ${digit ? "otp-input-filled" : ""
                  } ${errorIndex ? "otp-input-error" : ""}`}
              />
            ))}
          </div>

          {/* Timer */}
          <div className="otp-timer">
            <span>Code will expire in</span>
            <span
              className={`otp-timer-value ${secondsLeft === 0 ? "otp-timer-expired" : ""
                }`}
            >
              {formatTime(secondsLeft)}
            </span>
          </div>

          {/* Verify Button */}
          <Button
            type="button"
            className="otp-verify-btn"
            onClick={() => {
              const code = otp.join("");
              if (code.length < OTP_LENGTH) {
                setErrorIndex(true);
                Swal.fire({
                  icon: "warning",
                  title: "Incomplete Code",
                  text: `Please enter all ${OTP_LENGTH} digits.`
                });
                return;
              }
              handleVerify(code);
            }}>
            {loading ? (
              <Spinner
                animation="border"
                size="sm"
                className="otp-spinner"
              />
            ) : (
              <FaShieldAlt />
            )}
            {loading ? "Verifying..." : "Verify OTP"}
          </Button>

          {/* Divider */}
          <div className="otp-divider">OR</div>

          {/* Resend Button */}
          <p className="otp-subtext" style={{ marginBottom: 8 }}>
            Didn't receive the code?
          </p>
          <Button
            className="otp-resend-btn"
            onClick={handleResend}
            disabled={secondsLeft > 0 || resendLoading}
          >
            {resendLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FaRedo className="otp-resend-icon" />
            )}
            {secondsLeft > 0 ? `Resend OTP (${formatTime(secondsLeft)})` : "Resend OTP"}
          </Button>

          {/* Security Note */}
          <div className="otp-security-note">
            <FaShieldAlt />
            <span>
              For your security, please do not share this code with anyone.
            </span>
          </div>

          <div className="otp-footer-note">
            <FaLock /> Secured by Estate Experts
          </div>
        </div>
      </div>
    </div>
  );
}

export default OtpVerification;